import CloseToast from '@/images/CloseToast.svg';
import ToastV from '@/images/ToastV.svg';
import ToastW from '@/images/ToastW.svg';
import { getMixpanel } from '@/services/analytics';
import theme from '@/theme';
import { AppPathname, AppRouteNames } from '@/utils/types';
import styled from '@emotion/styled';
import { MouseEvent } from 'react';
import { CloseButtonProps, Id, ToastContent, toast as toastify, ToastContainer as ToastifyContainer, ToastOptions } from 'react-toastify';
import { MaskedIcon } from '../MaskedIcon/MaskedIcon';

interface ExtendedToastOptions extends ToastOptions {
    containerWidth?: number;
}

interface Toast {
    title?: string;
    message?: string;
    render?: ToastContent<unknown>;
    toastId?: Id;
    options?: ExtendedToastOptions;
    buttonText?: string;
    buttonAction?: () => void;
    prevId?: Id | null;
    mixpanelCustomProps?: Record<string, any>;
}

const position: ToastOptions['position'] = 'top-right';

const StyledToastContainer = styled(ToastifyContainer)`
    .Toastify__toast {
        border-radius: 8px;
    }
`;

const styles = {
    containerStyle: {
        top: '7em',
        '--toastify-color-progress-success': theme.colors.black.main,
        '--toastify-color-light': theme.colors.black.main,
        boxShadow: 'none',
    },
    textStyle: {
        fontWeight: 700,
        color: theme.colors.black[0],
    },
};

export const ToastContainer = () => (
    <StyledToastContainer
        position={position}
        autoClose={2000}
        style={styles.containerStyle}
        hideProgressBar={true}
        newestOnTop={false}
        closeOnClick={false}
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        closeButton={CloseButton}
    />
);

const defaultToastWidth = 320;

function getToastStyle(label: string = '', button?: string, message: string = '', containerWidth?: number) {
    const maxLabelLength = Math.max(label.length, message.length);
    const newWidth = containerWidth || maxLabelLength * 8 + 100 + (button ? 90 : 0);

    if (!newWidth || newWidth < defaultToastWidth) {
        return styles.textStyle;
    }
    const left = defaultToastWidth - newWidth;
    return { ...styles.textStyle, left, width: newWidth };
}

function getConfig(options: ExtendedToastOptions = {}, title?: string, button?: string, toastId?: Id, message?: string): ToastOptions<{}> {
    const style = getToastStyle(title, button, message, options.containerWidth);

    return {
        position,
        toastId,
        autoClose: 5000,
        hideProgressBar: true,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        icon: false,
        isLoading: false,
        ...options,
        style: {
            ...style,
            ...options.style,
        },
    };
}

const CloseButton = (props?: CloseButtonProps) => (
    <View style={{ width: 24 }}>
        <MaskedIcon
            cursor={'pointer'}
            size="24px"
            src={CloseToast}
            onClick={e => props?.closeToast(e as unknown as MouseEvent<HTMLElement, globalThis.MouseEvent>)}
        />
    </View>
);

const Icons = {
    error: <MaskedIcon size="24px" color={theme.colors.error.main} src={ToastW} />,
    info: <MaskedIcon size="24px" color={theme.colors.primary.main} src={ToastW} />,
    success: <img src={ToastV} width={24} height={24} />,
};

const Title = styled.span`
    margin-left: 8px;
    font-size: 1.6rem;
    font-weight: 700;
    color: '#FFFEFD' !important;
    flex-direction: row;
`;

const Message = styled.span`
    margin-left: 8px;
    font-size: 1.6rem;
    font-weight: 500;
    color: '#FFFEFD' !important;
    flex-direction: row;
`;

const View = styled.span`
    display: flex;
    align-items: center;
`;

const Button = styled.span`
    width: 90px;
    height: 34px;
    display: flex;
    padding: 7px 8px;
    margin-left: 24px;
    justify-content: center;
    align-items: center;
    gap: 8px;
    border-radius: 6px;
    background: rgba(241, 242, 246, 0.2);
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    padding: 0;
`;

const MessageContainer = styled.div`
    flex-direction: column;
    display: flex;
`;

const buildRenderComponent = (title?: string, icon?: JSX.Element, buttonText?: string, buttonAction?: () => void, message?: string) => (
    <View>
        {icon}
        <MessageContainer>
            {title && <Title>{title}</Title>}
            {buttonText && buttonAction && <Button onClick={buttonAction}>{buttonText} </Button>}
            {message && <Message>{message}</Message>}
        </MessageContainer>
    </View>
);

const trackToast = ({
    type,
    title,
    message,
    mixpanelCustomProps = {},
}: {
    type: 'error' | 'alert' | 'notification';
    title?: string;
    message?: string;
    mixpanelCustomProps?: Record<string, any>;
}) => {
    const mixpanel = getMixpanel();
    const page = AppRouteNames[window.location.pathname as AppPathname] || window.location.pathname;

    const data = {
        ['toast-type']: type,
        'toast-title': title || '',
        'toast-message': message || '',
        page,
        ...mixpanelCustomProps,
    };
    mixpanel?.track('toast-msg', data);
};

export const toast = {
    success: ({ toastId, title, options, buttonText, buttonAction, prevId, render, mixpanelCustomProps }: Toast) => {
        trackToast({ type: 'notification', title, mixpanelCustomProps });
        if (prevId) {
            toastify.dismiss(prevId);
        }
        return toastify.success(
            render ?? buildRenderComponent(title, Icons.success, buttonText, buttonAction),
            getConfig(options, title, buttonText, toastId)
        );
    },
    error: ({ toastId, title, options, buttonText, buttonAction, message, prevId, render, mixpanelCustomProps }: Toast) => {
        trackToast({ type: 'error', title, message, mixpanelCustomProps });
        if (prevId) {
            toastify.dismiss(prevId);
        }
        return toastify.error(
            render ?? buildRenderComponent(title, Icons.error, buttonText, buttonAction, message),
            getConfig(options, title, buttonText, toastId, message)
        );
    },
    info: ({ toastId, title, options, buttonText, buttonAction, message, prevId, render, mixpanelCustomProps }: Toast) => {
        trackToast({ type: 'alert', title, message, mixpanelCustomProps });
        if (prevId) {
            toastify.dismiss(prevId);
        }
        return toastify.info(
            render ?? buildRenderComponent(title, Icons.info, buttonText, buttonAction, message),
            getConfig(options, title, buttonText, toastId)
        );
    },
    loading: ({ title, render, options, prevId, mixpanelCustomProps }: Toast) => {
        trackToast({ type: 'notification', title, mixpanelCustomProps });
        if (prevId) {
            toastify.dismiss(prevId);
        }
        return toastify.loading(render ?? buildRenderComponent(title), { ...getConfig(), ...options });
    },
    dismiss: (id: Id) => toastify.dismiss(id),
};
